import type { GemPack } from '../services/JKGame_economy'

export interface ShopOverlayPack {
  pack: GemPack
  total: number
  price: string
  bonusLine: string
}

export interface ShopOverlayOptions {
  title: string
  loadingText: string
  backLabel: string
  buyLabel: string
  cancelLabel: string
  onBack: () => void
  onPackTap: (pack: GemPack, total: number) => void
}

export interface ShopOverlayHandle {
  setLoading: () => void
  setBalance: (text: string) => void
  setStatus: (text: string, tone?: 'good' | 'bad' | 'sub') => void
  setPacks: (rows: ShopOverlayPack[]) => void
  showConfirm: (message: string, onConfirm: () => void) => void
  closeConfirm: () => void
  setBusy: (busy: boolean) => void
  destroy: () => void
}

export function mountShopOverlay(options: ShopOverlayOptions): ShopOverlayHandle {
  const root = document.createElement('div')
  root.className = 'game-shop-overlay'

  const panel = document.createElement('section')
  panel.className = 'game-shop-panel'

  const title = document.createElement('h1')
  title.className = 'game-shop-title'
  title.textContent = options.title

  const balance = document.createElement('p')
  balance.className = 'game-shop-balance'

  const status = document.createElement('p')
  status.className = 'game-shop-status'

  const list = document.createElement('div')
  list.className = 'game-shop-list'

  const back = document.createElement('button')
  back.type = 'button'
  back.className = 'game-shop-btn'
  back.textContent = options.backLabel
  back.addEventListener('click', options.onBack)

  panel.append(title, balance, status, list, back)
  root.append(panel)
  document.body.append(root)

  let busy = false
  let confirmRoot: HTMLDivElement | undefined

  const setStatus = (text: string, tone: 'good' | 'bad' | 'sub' = 'sub'): void => {
    status.textContent = text
    status.hidden = !text
    status.dataset.tone = tone
  }

  const closeConfirm = (): void => {
    confirmRoot?.remove()
    confirmRoot = undefined
  }

  setStatus(options.loadingText)

  return {
    setLoading: () => {
      list.innerHTML = ''
      setStatus(options.loadingText)
    },
    setBalance: (text) => {
      balance.textContent = text
      balance.hidden = !text
    },
    setStatus,
    setPacks: (rows) => {
      list.innerHTML = ''
      if (rows.length > 0) setStatus('')
      for (const row of rows) {
        const card = document.createElement('button')
        card.type = 'button'
        card.className = 'game-shop-pack'
        card.disabled = busy
        card.addEventListener('click', () => {
          if (!busy) options.onPackTap(row.pack, row.total)
        })
        card.innerHTML = [
          '<span class="game-shop-pack-main">',
          `<strong>💎 ${escapeHtml(String(row.total))}</strong>`,
          `<em>${escapeHtml(row.bonusLine)}</em>`,
          '</span>',
          `<b>${escapeHtml(row.price)}</b>`,
        ].join('')
        list.append(card)
      }
    },
    showConfirm: (message, onConfirm) => {
      if (confirmRoot) return
      confirmRoot = document.createElement('div')
      confirmRoot.className = 'game-shop-confirm'
      const card = document.createElement('section')
      card.className = 'game-shop-confirm-card'
      const copy = document.createElement('p')
      copy.textContent = message
      const actions = document.createElement('div')
      actions.className = 'game-shop-confirm-actions'
      const cancel = button(options.cancelLabel, 'game-shop-btn', closeConfirm)
      const confirm = button(options.buyLabel, 'game-shop-btn game-shop-primary', () => {
        closeConfirm()
        onConfirm()
      })
      actions.append(cancel, confirm)
      card.append(copy, actions)
      confirmRoot.append(card)
      root.append(confirmRoot)
    },
    closeConfirm,
    setBusy: (nextBusy) => {
      busy = nextBusy
      for (const el of list.querySelectorAll<HTMLButtonElement>('button')) el.disabled = busy
    },
    destroy: () => {
      closeConfirm()
      root.remove()
    },
  }
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.className = className
  btn.textContent = label
  btn.addEventListener('click', onClick)
  return btn
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
